/**
 * Android hybrid native playback surface gate (P1b / #1584).
 * Progressive MP4 → Media3 under WebView when tenant flag + APK shell allow it.
 */

import { isNativeApkClientAtOrNewerThanOffer } from '@lucimark/shared/helpers/playerAppVersion'
import { NATIVE_HYBRID_HAL_WEDGE_DISABLE_KEY } from '@/constants/playerStorageKeys'
import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

/** Native shell `versionName` floor for hybrid present/preload/stage commands (stageHybridPlaybackFile ships in 0.0.13). */
export const MIN_NATIVE_PLAYBACK_SHELL_VERSION = '0.0.13'

export const NATIVE_PLAYBACK_SURFACE_STORAGE_KEY = 'LUCIMARK:NATIVE_PLAYBACK_SURFACE'

/** URL query override. `1` on, `0` off. */
export const NATIVE_PLAYBACK_SURFACE_QUERY_PARAM = 'lucimark_native_playback_surface'

/**
 * Debug override from query or localStorage, or `null` when unset (fall through to tenant).
 */
export function readNativePlaybackSurfaceDebugOverride(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null
): boolean | null {
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
    const query = params.get(NATIVE_PLAYBACK_SURFACE_QUERY_PARAM)
    if (query === '1') {
      return true
    }
    if (query === '0') {
      return false
    }
  }
  catch {
    // ignore
  }
  if (storage == null) {
    return null
  }
  try {
    const stored = storage.getItem(NATIVE_PLAYBACK_SURFACE_STORAGE_KEY)
    if (stored === 'false') {
      return false
    }
    if (stored === 'true') {
      return true
    }
  }
  catch {
    // ignore
  }
  return null
}

/** True when a prior OMX HAL wedge persisted device-local hybrid off (survives reload). */
export function readNativeHybridHalWedgeDisabled(
  storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null
): boolean {
  if (storage == null) {
    return false
  }
  try {
    return storage.getItem(NATIVE_HYBRID_HAL_WEDGE_DISABLE_KEY) === '1'
  }
  catch {
    return false
  }
}

/** Persist after OMX HAL wedge so reload does not re-arm Media3 on a wedged box. */
export function persistNativeHybridHalWedgeDisabled(
  storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null
): void {
  if (storage == null) {
    return
  }
  try {
    storage.setItem(NATIVE_HYBRID_HAL_WEDGE_DISABLE_KEY, '1')
  }
  catch {
    // private mode / quota
  }
}

/** Admin turned hybrid back on — allow one retry on this device. */
export function clearNativeHybridHalWedgeDisabled(
  storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null
): void {
  if (storage == null) {
    return
  }
  try {
    storage.removeItem(NATIVE_HYBRID_HAL_WEDGE_DISABLE_KEY)
  }
  catch {
    // ignore
  }
}

function isAndroidTauriRuntime(): boolean {
  return isTauriRuntime() && getPlatform() === 'android'
}

/**
 * Resolve whether native Media3 progressive playback is enabled.
 *
 * Precedence: query/localStorage `0` → off; must be Android Tauri; shell ≥ {@link MIN_NATIVE_PLAYBACK_SHELL_VERSION};
 * query/localStorage `1` → on; persisted OMX HAL wedge disable → off; else tenant `nativePlaybackSurface`.
 */
export function isNativePlaybackSurfaceEnabled(opts: {
  tenantFlag?: boolean | null
  shellVersionName?: string | null
  /** Override platform probe (tests). Default: live Android Tauri check. */
  isAndroidTauri?: boolean
  search?: string
  storage?: Storage | null
}): boolean {
  const debug = readNativePlaybackSurfaceDebugOverride(opts.search, opts.storage)
  if (debug === false) {
    return false
  }
  const android = opts.isAndroidTauri ?? isAndroidTauriRuntime()
  if (!android) {
    return false
  }
  const shell = (opts.shellVersionName ?? '').trim()
  if (
    shell === ''
    || !isNativeApkClientAtOrNewerThanOffer(MIN_NATIVE_PLAYBACK_SHELL_VERSION, shell)
  ) {
    return false
  }
  if (debug === true) {
    return true
  }
  if (readNativeHybridHalWedgeDisabled(opts.storage ?? null)) {
    return false
  }
  return opts.tenantFlag === true
}

type TNativePlaybackSurfaceGateOpts = Parameters<typeof isNativePlaybackSurfaceEnabled>[0]

/** Why the resolved gate is off — same precedence as {@link isNativePlaybackSurfaceEnabled}. */
function nativePlaybackSurfaceOffReason(opts: TNativePlaybackSurfaceGateOpts): 'web' | 'apk' | 'flag' | 'debug' | 'omx' {
  const debug = readNativePlaybackSurfaceDebugOverride(opts.search, opts.storage)
  if (debug === false) {
    return 'debug'
  }
  const android = opts.isAndroidTauri ?? isAndroidTauriRuntime()
  if (!android) {
    return 'web'
  }
  const shell = (opts.shellVersionName ?? '').trim()
  if (
    shell === ''
    || !isNativeApkClientAtOrNewerThanOffer(MIN_NATIVE_PLAYBACK_SHELL_VERSION, shell)
  ) {
    return 'apk'
  }
  if (readNativeHybridHalWedgeDisabled(opts.storage ?? null)) {
    return 'omx'
  }
  return 'flag'
}

/** Compact debug-overlay chip: Android mark when on; off reason when native Media3 is not active. */
export function nativePlaybackSurfaceOverlayChip(opts: TNativePlaybackSurfaceGateOpts): {
  enabled: boolean
  /** Empty when on (icon-only). Off: `desligado · web|apk|flag|debug`. */
  detail: string
  ariaLabel: string
} {
  const enabled = isNativePlaybackSurfaceEnabled(opts)
  if (enabled) {
    return { enabled: true, detail: '', ariaLabel: 'Android · ligado' }
  }
  const reason = nativePlaybackSurfaceOffReason(opts)
  return {
    enabled: false,
    detail: `desligado · ${reason}`,
    ariaLabel: `Android · desligado · ${reason}`
  }
}

/**
 * When a native Media3 progressive clip is on-air, punch through opaque player chrome
 * (`html`/`body`/`bg-black` shells) so the TextureView under the WebView is visible.
 */
export const NATIVE_PLAYBACK_UNDERLAY_ATTR = 'data-lucimark-native-underlay'

export function setNativePlaybackUnderlayChrome(
  active: boolean,
  root: Document | null = typeof document !== 'undefined' ? document : null,
): void {
  const el = root?.documentElement
  if (el == null) {
    return
  }
  if (active) {
    el.setAttribute(NATIVE_PLAYBACK_UNDERLAY_ATTR, '1')
  }
  else {
    el.removeAttribute(NATIVE_PLAYBACK_UNDERLAY_ATTR)
  }
}

export function isNativePlaybackUnderlayChromeActive(
  root: Document | null = typeof document !== 'undefined' ? document : null,
): boolean {
  return root?.documentElement?.getAttribute(NATIVE_PLAYBACK_UNDERLAY_ATTR) === '1'
}

/**
 * After native `present`, if no `onFirstFrame` arrives in this window, treat OMX as
 * process-wide wedged (Allwinner HAL lives in mediaserver — not a per-URN bad file).
 */
export const HYBRID_FIRST_FRAME_WATCH_MS = 6000

/** Sentry tag `video_playback_hybrid_error_code` for the first-frame timeout demote. */
export const HYBRID_OMX_FIRST_FRAME_TIMEOUT_CODE = 'HYBRID_OMX_FIRST_FRAME_TIMEOUT'

/** Kotlin BUFFERING watchdog: release + `onError` (no OMX remount). Session HAL wedge. */
export const HYBRID_BUFFERING_STALL_CODE = 'HYBRID_BUFFERING_STALL'

/** Sentry when Device Owner `dpm.reboot` actually fires / is skipped (layer 3). */
export const HYBRID_OMX_HAL_REBOOT_FIRED_CODE = 'HYBRID_OMX_HAL_REBOOT_FIRED'
export const HYBRID_OMX_HAL_REBOOT_SKIPPED_CODE = 'HYBRID_OMX_HAL_REBOOT_SKIPPED'

/** Process-wide HAL wedge — not a per-URN bad file. */
export function isHybridHalWedgeErrorCode(code: string | null | undefined): boolean {
  const c = (code ?? '').toUpperCase()
  return c === HYBRID_OMX_FIRST_FRAME_TIMEOUT_CODE || c === HYBRID_BUFFERING_STALL_CODE
}

/** Must match `OmxHalWedgeReboot.COOLDOWN_MS` in the Android plugin. */
export const OMX_HAL_WEDGE_REBOOT_COOLDOWN_MS = 6 * 60 * 60 * 1000

/** Device Owner reboot policy (Kotlin SharedPreferences is the durable store). */
export function shouldRebootOmxHalWedge(opts: {
  deviceOwner: boolean
  lastRebootAtMs: number
  nowMs: number
  cooldownMs?: number
}): boolean {
  if (!opts.deviceOwner) {
    return false
  }
  const cooldown = opts.cooldownMs ?? OMX_HAL_WEDGE_REBOOT_COOLDOWN_MS
  if (opts.lastRebootAtMs <= 0) {
    return true
  }
  return opts.nowMs - opts.lastRebootAtMs >= cooldown
}

/** Skip native present/preload/stage for this URN (per-URN error, process-wide HAL, or reload). */
export function shouldSkipNativeHybridSurface(opts: {
  urn: string
  failedUrns: ReadonlySet<string>
  halWedged: boolean
  recoveryReloadArmed?: boolean
}): boolean {
  return opts.halWedged
    || opts.recoveryReloadArmed === true
    || opts.failedUrns.has(opts.urn)
}

/**
 * Watch for Media3 `onFirstFrame` after `present`. Arm/disarm from VBuffer;
 * `poll` is the unit-testable timeout check (no Vue mount).
 */
export function createHybridFirstFrameWatch(opts?: {
  timeoutMs?: number
  now?: () => number
}): {
  arm: (urn: string) => void
  disarm: () => void
  isArmed: () => boolean
  /** Returns the armed URN when `nowMs` is past the deadline; otherwise null. */
  poll: (nowMs: number) => string | null
} {
  const timeoutMs = opts?.timeoutMs ?? HYBRID_FIRST_FRAME_WATCH_MS
  const nowFn = opts?.now ?? Date.now
  let armedUrn: string | null = null
  let armedAtMs = 0

  return {
    arm(urn: string) {
      armedUrn = urn
      armedAtMs = nowFn()
    },
    disarm() {
      armedUrn = null
      armedAtMs = 0
    },
    isArmed() {
      return armedUrn != null
    },
    poll(nowMs: number) {
      if (armedUrn == null) {
        return null
      }
      if (nowMs - armedAtMs < timeoutMs) {
        return null
      }
      const urn = armedUrn
      armedUrn = null
      armedAtMs = 0
      return urn
    }
  }
}
